import { Injector, isSignal, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { runWithInjectionContext } from '@kaseya-utils';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { ShellEvent } from '../models/shell-event';

/**
 * Creates a broadcast event suitable for passing to a remote integration.
 *
 * Signal sources emit the current value synchronously on every new subscription
 * via `startWith`, with future changes tracked via `toObservable`.
 *
 * Observable sources are eagerly subscribed from the moment the event is created,
 * with the last emitted value replayed to late subscribers via a `ReplaySubject`.
 *
 * Must be called within an Angular injection context (or pass an explicit `injector`).
 */
export function shellEvent<T>(source: Signal<T> | Observable<T>, injector?: Injector): ShellEvent<T> {
  const createEvent = (): ShellEvent<T> => {
    const source$ = isSignal(source) ? toObservable(source).pipe(startWith(source())) : source;

    const replay = new ReplaySubject<T>(1);
    source$.pipe(takeUntilDestroyed(), distinctUntilChanged()).subscribe(replay);
    return replay.asObservable() as ShellEvent<T>;
  };

  return runWithInjectionContext(createEvent, injector);
}
